import { VoucherCategory } from '../../vouchers/index.js';
import { isCalculatedInterest } from '../../vouchers/setup.js';
import { MS_PER_DAY } from '../engine-v2/dates.js';
import helpers from '../index.js';
export var LedgerAccount;
(function (LedgerAccount) {
    LedgerAccount["Principal"] = "principal";
    LedgerAccount["Interest"] = "interest";
    LedgerAccount["Costs"] = "costs";
})(LedgerAccount || (LedgerAccount = {}));
export const LedgerAccounts = [
    LedgerAccount.Principal,
    LedgerAccount.Interest,
    LedgerAccount.Costs,
];
export var LedgerRowType;
(function (LedgerRowType) {
    LedgerRowType["CaseVoucher"] = "CASE_VOUCHER";
    LedgerRowType["Interest"] = "INTEREST";
})(LedgerRowType || (LedgerRowType = {}));
const zeroAmounts = () => ({
    [LedgerAccount.Principal]: 0,
    [LedgerAccount.Interest]: 0,
    [LedgerAccount.Costs]: 0,
});
const addAmounts = (a, b) => ({
    [LedgerAccount.Principal]: a[LedgerAccount.Principal] + b[LedgerAccount.Principal],
    [LedgerAccount.Interest]: a[LedgerAccount.Interest] + b[LedgerAccount.Interest],
    [LedgerAccount.Costs]: a[LedgerAccount.Costs] + b[LedgerAccount.Costs],
});
export const sumAmounts = (amounts) => amounts[LedgerAccount.Principal] +
    amounts[LedgerAccount.Interest] +
    amounts[LedgerAccount.Costs];
// Transactions have no account: they are settled against the other accounts
export const getLedgerAccount = (category) => {
    switch (category) {
        case VoucherCategory.PRINCIPAL:
            return LedgerAccount.Principal;
        case VoucherCategory.INTEREST:
            return LedgerAccount.Interest;
        case VoucherCategory.COST:
        case VoucherCategory.COURTCOST:
            return LedgerAccount.Costs;
        default:
            return null;
    }
};
const writeOffReasons = ['expired', 'irrecoverable'];
// Zero on engine 1, which doesn't write debt off
const writeOffsOf = (caseVoucher) => ({
    expired: caseVoucher.expiredAmount ?? 0,
    irrecoverable: caseVoucher.irrecoverableAmount ?? 0,
});
// What of the accruals booked in [from, to) was written off
const writtenOffBetween = (accruals, from, to) => accruals.reduce((sum, accrual) => {
    const start = Math.max(from, accrual.from);
    const end = Math.min(to, accrual.to);
    if (start >= end) {
        return sum;
    }
    const bookedFrom = (accrual.perDay * (start - accrual.from)) / MS_PER_DAY;
    const bookedTo = (accrual.perDay * (end - accrual.from)) / MS_PER_DAY;
    return (sum +
        Math.max(0, Math.min(bookedTo, accrual.paid + accrual.writtenOff) -
            Math.max(bookedFrom, accrual.paid)));
}, 0);
/**
 * Replays the interest calculation and distribution and records, in
 * chronological order, every case voucher as it is booked and the interest
 * accrued per month (and up to every case voucher date). Each shows what of
 * it has expired since, and the running balances after it count only what is
 * still owed. What became irrecoverable is left out, as engine 1 drops it.
 */
export const calculate = async (caseVouchers, documents, economyPolicy, fixedDistributions, conditions, untilISOString, options = {}) => {
    const steps = [];
    // Amount and distribution of every case voucher as of the previous
    // breakpoint, so each breakpoint only reports what changed since
    const previousState = new Map();
    const onBreakpoint = (state, from, to) => {
        const balances = zeroAmounts();
        const appliedTo = zeroAmounts();
        const writtenOff = {
            expired: zeroAmounts(),
            irrecoverable: zeroAmounts(),
        };
        let accruedInterest = 0;
        // The distributor appends one case voucher per period, so at most one is
        // unseen here
        let appendedCaseVoucher;
        for (const caseVoucher of state) {
            const previous = previousState.get(caseVoucher.caseVoucherId);
            if (previous === undefined) {
                appendedCaseVoucher = caseVoucher;
            }
            const writeOffs = writeOffsOf(caseVoucher);
            const newlyWrittenOff = (reason) => writeOffs[reason] - (previous?.writeOffs[reason] ?? 0);
            const account = getLedgerAccount(caseVoucher.category);
            if (account !== null) {
                balances[account] += caseVoucher.distributionAmount - caseVoucher.amount;
                appliedTo[account] +=
                    caseVoucher.distributionAmount - (previous?.distributionAmount ?? 0);
                for (const reason of writeOffReasons) {
                    writtenOff[reason][account] += newlyWrittenOff(reason);
                }
            }
            // A case voucher of interest is booked on its own row; calculated
            // interest accrues, net of what was written off, so add that back
            if (account === LedgerAccount.Interest &&
                (previous !== undefined || isCalculatedInterest(caseVoucher))) {
                accruedInterest -=
                    caseVoucher.amount -
                        (previous?.amount ?? 0) -
                        newlyWrittenOff('expired') -
                        newlyWrittenOff('irrecoverable');
            }
            previousState.set(caseVoucher.caseVoucherId, {
                amount: caseVoucher.amount,
                distributionAmount: caseVoucher.distributionAmount,
                writeOffs,
            });
        }
        // Running balances before the period's write-offs
        const running = { ...balances };
        for (const reason of writeOffReasons) {
            for (const account of LedgerAccounts) {
                running[account] += writtenOff[reason][account];
            }
        }
        let caseVoucherRow = null;
        if (appendedCaseVoucher !== undefined) {
            // As it was entered, before anything of it was written off
            const writeOffs = writeOffsOf(appendedCaseVoucher);
            const amount = isCalculatedInterest(appendedCaseVoucher)
                ? 0
                : -(appendedCaseVoucher.amount -
                    writeOffs.expired -
                    writeOffs.irrecoverable);
            if (amount !== 0 ||
                sumAmounts(appliedTo) !== 0 ||
                options.includeZeroVouchers === true) {
                caseVoucherRow = {
                    type: LedgerRowType.CaseVoucher,
                    date: appendedCaseVoucher.date,
                    caseVoucher: {
                        caseVoucherId: appendedCaseVoucher.caseVoucherId,
                        voucherTypeId: appendedCaseVoucher.voucherTypeId,
                        category: appendedCaseVoucher.category,
                        setting: appendedCaseVoucher.setting,
                        text: appendedCaseVoucher.text,
                        label: appendedCaseVoucher.label,
                        referenceId: appendedCaseVoucher.referenceId,
                    },
                    amount,
                    appliedTo,
                };
            }
        }
        steps.push({
            from,
            to,
            caseVoucherRow,
            accruedInterest,
            running,
            writtenOff,
        });
    };
    const calculated = await helpers.interest.calculate(caseVouchers, documents, economyPolicy, fixedDistributions, untilISOString, {
        ...conditions,
        monthStartBreakpoints: true,
        breakPointCallBack: onBreakpoint,
        freezeLimitation: options.freezeLimitation,
        writeOffAccruals: true,
    });
    // What expired, and what became irrecoverable, by now: of a case voucher as
    // a whole, of calculated interest by the days it accrued on
    const calculatedById = new Map(calculated.map((caseVoucher) => [caseVoucher.caseVoucherId, caseVoucher]));
    const expiredAccruals = calculated.flatMap((caseVoucher) => caseVoucher.expiredAccruals ?? []);
    const irrecoverableAccruals = calculated.flatMap((caseVoucher) => caseVoucher.irrecoverableAccruals ?? []);
    // A statement revises the balances to now: what expired or became
    // irrecoverable leaves them from the row it was of. A forecast shows them as
    // they stood: what is written off leaves them when it is, with that period's
    // interest.
    const revised = options.asTheyStood !== true;
    const rows = [];
    // What the rows leave out that the replay had yet to write off
    const pending = zeroAmounts();
    for (const step of steps) {
        // Out of the balances from the row it was of: what expired since, and
        // what became irrecoverable, which is not shown at all
        const removed = zeroAmounts();
        let caseVoucherRow = null;
        if (step.caseVoucherRow !== null) {
            const { caseVoucher } = step.caseVoucherRow;
            const calculatedVoucher = revised && !isCalculatedInterest(caseVoucher)
                ? calculatedById.get(caseVoucher.caseVoucherId)
                : undefined;
            const expiredSince = calculatedVoucher?.expiredAmount ?? 0;
            const irrecoverable = calculatedVoucher?.irrecoverableAmount ?? 0;
            caseVoucherRow = {
                ...step.caseVoucherRow,
                amount: step.caseVoucherRow.amount - irrecoverable,
                expiredSince,
            };
            const account = getLedgerAccount(caseVoucher.category);
            if (account !== null) {
                removed[account] += expiredSince + irrecoverable;
            }
        }
        const interestExpired = revised
            ? writtenOffBetween(expiredAccruals, step.from, step.to)
            : 0;
        const interestIrrecoverable = revised
            ? writtenOffBetween(irrecoverableAccruals, step.from, step.to)
            : 0;
        const running = { ...step.running };
        for (const account of LedgerAccounts) {
            running[account] -= pending[account] + removed[account];
        }
        if (caseVoucherRow !== null &&
            (caseVoucherRow.amount !== 0 ||
                sumAmounts(caseVoucherRow.appliedTo) !== 0 ||
                options.includeZeroVouchers === true)) {
            rows.push({
                ...caseVoucherRow,
                balances: {
                    ...running,
                    [LedgerAccount.Interest]: running[LedgerAccount.Interest] - step.accruedInterest,
                },
            });
        }
        running[LedgerAccount.Interest] -= interestExpired + interestIrrecoverable;
        removed[LedgerAccount.Interest] += interestExpired + interestIrrecoverable;
        const writtenOffThen = zeroAmounts();
        if (!revised) {
            for (const account of LedgerAccounts) {
                writtenOffThen[account] =
                    step.writtenOff.expired[account] +
                        step.writtenOff.irrecoverable[account];
                running[account] -= writtenOffThen[account];
            }
        }
        const period = {
            from: new Date(step.from).toISOString(),
            to: new Date(step.to).toISOString(),
        };
        const interestAmount = step.accruedInterest -
            interestIrrecoverable -
            writtenOffThen[LedgerAccount.Interest];
        if (interestAmount !== 0) {
            rows.push({
                type: LedgerRowType.Interest,
                date: period.to,
                period,
                amount: interestAmount,
                expiredSince: interestExpired,
                balances: { ...running },
            });
        }
        if (revised) {
            for (const account of LedgerAccounts) {
                pending[account] +=
                    removed[account] -
                        step.writtenOff.expired[account] -
                        step.writtenOff.irrecoverable[account];
            }
        }
    }
    const lastRow = rows[rows.length - 1];
    return {
        rows,
        balances: lastRow?.balances ?? zeroAmounts(),
        totals: {
            amount: rows.reduce((sum, row) => sum + row.amount, 0),
            expiredSince: rows.reduce((sum, row) => sum + row.expiredSince, 0),
            appliedTo: rows.reduce((sum, row) => row.type === LedgerRowType.CaseVoucher
                ? addAmounts(sum, row.appliedTo)
                : sum, zeroAmounts()),
        },
    };
};
//# sourceMappingURL=index.js.map